import { useEffect, useRef, useState } from "hono/jsx";

interface MenuProps {
  isHost?: boolean;
  isPublic?: boolean;
}

export function Menu({
  isHost = false,
  isPublic = false,
}: MenuProps) {
  const menuRef = useRef<HTMLDialogElement>(null);

  // Local state
  const [localIsPublic, setLocalIsPublic] = useState(isPublic);
  const [localViewDistance, setLocalViewDistance] = useState(16);
  const [localHighGraphics, setLocalHighGraphics] = useState(true);
  const [localTimeSpeed, setLocalTimeSpeed] = useState(2); // Default to low
  const [localMoonPhase, setLocalMoonPhase] = useState(0); // 0-28 lunar cycle
  const [localWeatherType, setLocalWeatherType] = useState("clear");
  const [localFogDensity, setLocalFogDensity] = useState(0);

  // Initialize from localStorage if available
  useEffect(() => {
    if (typeof window !== "undefined") {
      // View Distance
      const savedVD = localStorage.getItem("vox:viewDistance");
      if (savedVD) {
        const dist = parseInt(savedVD);
        setLocalViewDistance(dist);
        document.dispatchEvent(
          new CustomEvent("vox:set:viewdistance", { detail: dist }),
        );
      } else {
        document.dispatchEvent(
          new CustomEvent("vox:set:viewdistance", { detail: 16 }),
        );
      }

      // High Graphics (Shaders) - default ON (true)
      const savedHG = localStorage.getItem("vox:highGraphics");
      const isHigh = savedHG === null ? true : savedHG === "true";
      setLocalHighGraphics(isHigh);
      document.dispatchEvent(
        new CustomEvent("vox:graphics:toggle", { detail: isHigh }),
      );
    }
  }, []);

  // Sync moonPhase and timeSpeed from server
  useEffect(() => {
    const handleWsMessage = (e: CustomEvent) => {
      const msg = e.detail;
      if (msg.type === "INIT" || msg.type === "TIME_UPDATE") {
        if (msg.moonPhase !== undefined) setLocalMoonPhase(msg.moonPhase);
        if (msg.timeSpeed !== undefined) setLocalTimeSpeed(msg.timeSpeed);
        if (msg.weather) {
          setLocalWeatherType(msg.weather.type);
          setLocalFogDensity(msg.weather.fogDensity);
        }
      } else if (msg.type === "WEATHER_UPDATE" && msg.weather) {
        setLocalWeatherType(msg.weather.type);
        setLocalFogDensity(msg.weather.fogDensity);
      }
    };
    document.addEventListener(
      "vox:ws:message",
      handleWsMessage as EventListener,
    );
    return () =>
      document.removeEventListener(
        "vox:ws:message",
        handleWsMessage as EventListener,
      );
  }, []);

  // Debounce Ref
  const lastEscRef = useRef<number>(0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Esc") {
        const now = Date.now();
        // Debounce: 200ms
        if (now - (lastEscRef.current ?? 0) < 200) {
          return;
        }
        lastEscRef.current = now;

        if (e.repeat) return;
        if (!menuRef.current) return;

        e.preventDefault();

        if (menuRef.current?.open) {
          menuRef.current?.close();
          document.dispatchEvent(new CustomEvent("vox:menu:closed"));
        } else {
          menuRef.current?.showModal();
          document.dispatchEvent(new CustomEvent("vox:menu:opened"));
        }
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    setLocalIsPublic(isPublic);

    // deno-lint-ignore no-explicit-any
    const onStateUpdate = (e: any) => {
      const detail = e.detail;
      if (detail.isPublic !== undefined) setLocalIsPublic(detail.isPublic);
      if (detail.timeSpeed !== undefined) setLocalTimeSpeed(detail.timeSpeed);
    };

    document.addEventListener("vox:state:update", onStateUpdate);
    return () =>
      document.removeEventListener("vox:state:update", onStateUpdate);
  }, [isPublic]);

  const togglePublic = () => {
    const newValue = !localIsPublic;
    setLocalIsPublic(newValue);
    document.dispatchEvent(
      new CustomEvent("vox:set:public", { detail: newValue }),
    );
  };

  const setViewDistance = (distance: number) => {
    setLocalViewDistance(distance);
    localStorage.setItem("vox:viewDistance", distance.toString());
    document.dispatchEvent(
      new CustomEvent("vox:set:viewdistance", { detail: distance }),
    );
  };

  const toggleGraphics = () => {
    const newValue = !localHighGraphics;
    setLocalHighGraphics(newValue);
    localStorage.setItem("vox:highGraphics", newValue.toString());
    document.dispatchEvent(
      new CustomEvent("vox:graphics:toggle", { detail: newValue }),
    );
  };

  useEffect(() => {
    const onPointerLockChange = () => {
      // Optional: Auto-open menu logic if desired
    };
    document.addEventListener("pointerlockchange", onPointerLockChange);
    return () =>
      document.removeEventListener("pointerlockchange", onPointerLockChange);
  }, []);

  return (
    <dialog
      ref={menuRef}
      className="fixed inset-0 z-50 w-full h-full bg-transparent backdrop:bg-black/60 p-0 m-0 max-w-none max-h-none open:flex items-center justify-center pointer-events-auto"
      onClose={() => document.dispatchEvent(new CustomEvent("vox:menu:closed"))}
      onClick={(e) => {
        if (e.target === menuRef.current) {
          menuRef.current?.close();
          document.dispatchEvent(new CustomEvent("vox:menu:closed"));
        }
      }}
    >
      <div className="bg-base-100 text-center border-2 border-primary/20 shadow-2xl p-8 rounded-2xl w-full max-w-lg mx-4 relative cursor-default isolate overflow-hidden">
        <h2 className="text-3xl font-black mb-6 tracking-tighter uppercase text-primary">
          Voxhole Menu
        </h2>

        <div className="space-y-6">
          {/* All Users Controls */}
          <div className="bg-base-200 p-4 rounded-xl border border-white/5">
            <p className="text-xs uppercase font-black opacity-30 tracking-widest mb-4">
              Settings
            </p>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-bold text-base">
                  View Distance
                </span>
                <span className="label-text-alt opacity-50 font-mono">
                  {localViewDistance} chunks
                </span>
              </label>
              <input
                type="range"
                min="1"
                max="20"
                step="1"
                className="range range-primary"
                value={localViewDistance}
                onChange={(e) =>
                  setViewDistance(
                    parseInt((e.target as HTMLInputElement).value),
                  )}
              />
            </div>

            <label className="label cursor-pointer mt-4 border-t border-white/5 pt-4">
              <span className="label-text font-bold text-base">
                High Graphics (Water Shader)
              </span>
              <input
                type="checkbox"
                className="toggle toggle-secondary"
                checked={localHighGraphics}
                onChange={toggleGraphics}
              />
            </label>
          </div>

          {/* Host Only Controls */}
          {isHost && (
            <div className="bg-base-200 p-4 rounded-xl border border-white/5">
              <p className="text-xs uppercase font-black opacity-30 tracking-widest mb-4">
                Host Controls
              </p>

              <label className="label cursor-pointer bg-base-100 p-4 rounded-lg border border-white/5 shadow-inner">
                <span className="label-text font-black text-lg">
                  Public Room
                </span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary toggle-lg"
                  checked={localIsPublic}
                  onChange={togglePublic}
                />
              </label>

              <div className="mt-4 border-t border-white/5 pt-4 space-y-4">
                <p className="text-xs uppercase font-bold opacity-50 tracking-wider">
                  Environment
                </p>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-bold">Time Speed</span>
                    <span className="label-text-alt opacity-50 font-mono">
                      {localTimeSpeed}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    className="range range-secondary range-sm"
                    value={localTimeSpeed}
                    onChange={(e) => {
                      const val = parseInt(
                        (e.target as HTMLInputElement).value,
                      );
                      setLocalTimeSpeed(val); // Optimistic update
                      document.dispatchEvent(
                        new CustomEvent("vox:set:timespeed", { detail: val }),
                      );
                    }}
                  />
                  <div className="flex justify-between text-xs px-1 opacity-50 font-mono mt-1">
                    <span>Freeze</span>
                    <span>Chill</span>
                    <span>Fast</span>
                  </div>
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-bold">Moon Phase</span>
                    <span className="label-text-alt opacity-50 font-mono">
                      Day {localMoonPhase}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="28"
                    step="1"
                    className="range range-accent range-sm"
                    value={localMoonPhase}
                    onChange={(e) => {
                      const val = parseInt(
                        (e.target as HTMLInputElement).value,
                      );
                      setLocalMoonPhase(val);
                      document.dispatchEvent(
                        new CustomEvent("vox:set:moonphase", { detail: val }),
                      );
                    }}
                  />
                  <div className="w-full grid grid-cols-5 text-xs px-1 opacity-50 font-mono mt-1">
                    <span className="text-left">New</span>
                    <span className="text-center">1st Qtr</span>
                    <span className="text-center">Full</span>
                    <span className="text-center">3rd Qtr</span>
                    <span className="text-right">New</span>
                  </div>
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-bold">Weather</span>
                  </label>
                  <select
                    className="select select-bordered select-sm w-full"
                    value={localWeatherType}
                    onChange={(e) => {
                      const type = (e.target as HTMLSelectElement).value;
                      setLocalWeatherType(type);
                      document.dispatchEvent(
                        new CustomEvent("vox:set:weather", {
                          detail: { type, fogDensity: localFogDensity },
                        }),
                      );
                    }}
                  >
                    <option value="clear">Clear</option>
                    <option value="cloudy">Cloudy</option>
                    <option value="overcast">Overcast</option>
                    <option value="rain">Rain</option>
                    <option value="snow">Snow</option>
                  </select>
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-bold">Fog Density</span>
                    <span className="label-text-alt opacity-50 font-mono">
                      {localFogDensity}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    className="range range-info range-sm"
                    value={localFogDensity}
                    onChange={(e) => {
                      const val = parseInt(
                        (e.target as HTMLInputElement).value,
                      );
                      setLocalFogDensity(val);
                      document.dispatchEvent(
                        new CustomEvent("vox:set:weather", {
                          detail: { type: localWeatherType, fogDensity: val },
                        }),
                      );
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() =>
                      document.dispatchEvent(
                        new CustomEvent("vox:set:time", { detail: 1000 }),
                      )}
                  >
                    🌄 Dawn
                  </button>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() =>
                      document.dispatchEvent(
                        new CustomEvent("vox:set:time", { detail: 6000 }),
                      )}
                  >
                    ☀️ Noon
                  </button>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() =>
                      document.dispatchEvent(
                        new CustomEvent("vox:set:time", { detail: 12000 }),
                      )}
                  >
                    🌅 Dusk
                  </button>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() =>
                      document.dispatchEvent(
                        new CustomEvent("vox:set:time", { detail: 18000 }),
                      )}
                  >
                    🌙 Midnight
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-action justify-center mt-8">
          <button
            type="button"
            className="btn btn-primary btn-lg px-12 font-black uppercase tracking-tighter shadow-xl"
            onClick={() => {
              menuRef.current?.close();
              document.dispatchEvent(new CustomEvent("vox:menu:closed"));
            }}
          >
            Resume
          </button>
        </div>

        <p className="text-[10px] uppercase font-black opacity-20 mt-6 tracking-widest">
          [ESC] to toggle
        </p>
      </div>
    </dialog>
  );
}
